<script setup lang="ts">
import { Input } from '@/components/ui/input';
import {
    FormControl,
    FormField,
    FormItem,
    FormMessage,
} from '@/components/ui/form'
import AutoFormLabel from '@/ui-plus/auto-form/AutoFormLabel.vue'
import { ref, watch, onMounted, computed } from "vue";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AutoForm } from "@/ui-plus/auto-form";
import { toTypedSchema } from "@vee-validate/zod";
import { useForm } from "vee-validate";
import { z } from "zod";
import { useFormPlus } from "@/ui-plus/form/useFormPlus";
import AutoFormFieldCountry from "@/ui-plus/auto-form/AutoFormFieldCountry.vue";
import { DependencyType } from "@/ui-plus/auto-form/interface";
import GeolocationMap from "@/ui-plus/geolocation-selection-map/GeolocationMap.vue";
import { loadLeafletCDN } from "@/leaflet/loadLeafletCDN";
import {
    addressSchema, type AddressType,
} from "@lib/zod/userAddressSchema";
import {
    DoorOpen,
} from "lucide-vue-next";
import AutoFormFieldDropoffOptions from "@/ui-plus/auto-form/AutoFormFieldDropoffOptions.vue";
import AutoFormFieldPhone from "@/ui-plus/PhoneNumber/AutoFormFieldPhone.vue";
import { getAddress } from "@lib/utils/checkGeocodeDistance";
import { generateUUID } from "@lib/utils/generateUUID";
import { allCountriesAndStatesWithMultipleSalesTaxJurisdiction } from "@lib/utils/sales_tax_rates";

const REMEMBER_LAST_PHONE_NUMBER = "REMEMBER_LAST_PHONE_NUMBER_INPUT_VALUE"

const props = defineProps<{
    address?: Partial<AddressType>;
}>();

const emit = defineEmits<{
    addressFormSubmitted: [address: AddressType],
}>();

const isMapDialogOpen = ref(false);
const lat = ref<number | null>(props.address?.lat || null);
const lng = ref<number | null>(props.address?.lng || null);

// Form setup
const schema = addressSchema
    .omit({
        addressId: true,
        lat: true,
        lng: true,
        lastUpdatedTS: true,
    })

const form = useForm({
    validationSchema: toTypedSchema(schema),
});

const { withSubmit } = useFormPlus(form, {
    persistenceFields: {
        phoneNumber: REMEMBER_LAST_PHONE_NUMBER
    }
});

// Form labels
const address1Label = ref('Street address (Address 1)');
const address2Label = ref('Apt, suite, unit, building, floor, etc. (Address 2)');
const hideAddress2Label = ref(false);
const addressStateLabel = ref('State/Province/Territory');
const addressCityLabel = ref('City');
const addressPostalCodeLabel = ref('Postcode/Zip');

// Countries that have states
const COUNTRIES_WITH_STATES = [
    'US', 'CA', 'AU', 'BR', 'IN', 'MX', 'AR', 'MY', 'ID', 'RU', 'CN', 'JP', 'DE',
    'ES', 'IT', 'GB', 'FR', 'PH', 'PK', 'NG', 'VN', 'ZA', 'TH', 'MM', 'KR', 'CO', 'VE'
];

// form.resetForm();
async function onFormSubmit(_values: z.infer<typeof schema>) {
    await withSubmit(async () => {
        isMapDialogOpen.value = true;
    });
}

async function onMapConfirm(result: { lat: number; lng: number }) {
    isMapDialogOpen.value = false;
    lat.value = result.lat;
    lng.value = result.lng;

    // Make sure all required fields are included
    const completeAddress = {
        ...form.values,
        addressId: props.address?.addressId || generateUUID(),
        lat: lat.value,
        lng: lng.value,
    } as AddressType;

    emit('addressFormSubmitted', completeAddress);
}

// Watchers for form labels based on country
const updateFormLabels = (country: string) => {
    if (country === 'US') {
        address1Label.value = 'Street address';
        address2Label.value = 'Apt, suite, unit, building, floor, etc.';
        hideAddress2Label.value = true;
        addressCityLabel.value = 'City';
        addressStateLabel.value = 'State';
        addressPostalCodeLabel.value = 'Postcode';
    } else if (country === 'GB') {
        address1Label.value = 'Address line 1 (or company name)';
        address2Label.value = 'Address line 2 (optional)';
        addressCityLabel.value = 'Town/City';
        addressStateLabel.value = 'State/County (if applicable)';
        hideAddress2Label.value = false;
        addressPostalCodeLabel.value = 'Postcode';
    } else if (country === 'CA') {
        address1Label.value = 'Address';
        address2Label.value = 'Apt, suite, unit, building (Address 2)';
        hideAddress2Label.value = true;
        addressStateLabel.value = 'Province/Territory';
        addressCityLabel.value = 'City';
        addressPostalCodeLabel.value = 'Postal code';
    } else {
        address1Label.value = 'Street address (Address 1)';
        address2Label.value = 'Apt, suite, unit, building, floor, etc. (Address 2)';
        addressStateLabel.value = 'State/Province/Territory';
        addressCityLabel.value = 'City';
        hideAddress2Label.value = true;
        addressPostalCodeLabel.value = 'Postcode/Zip';
    }
};

// Watch for country changes
watch(() => form.values.country, (newCountry) => {
    if (newCountry) {
        updateFormLabels(newCountry);
    }
});

// Initialize Leaflet on component mount
onMounted(() => {
    form.setValues(props.address || {});
    loadLeafletCDN(true);
});

const isStateFieldVisible = computed(() => {
    return COUNTRIES_WITH_STATES.includes(form.values.country ?? '');
});

const computedStates = computed(() => {
    return Object.entries(
        allCountriesAndStatesWithMultipleSalesTaxJurisdiction[form.values.country ?? ''] || {}
    ).sort() || [];
});


// TODO: add autofill btn with known last informations
// async function autofillLastKnownInformations() {
//     const ip = await ipwhois();
//     // const userHasAddress = userDataSyncedRef.value?.addresses && Object.keys(userDataSyncedRef.value?.addresses).length > 0;
//     const { iframeId } = await callParent('zmgNewAccessoryLayerWidget', {
//         path: 'widgets/dialogs/user-address-form',
//         isChannelRequired: true,
//     });
//     createParentVueBridge<UserAddressDialog>({
//         channelId: iframeId,
//         props: {
//             title: 'Add Address',
//             address: {
//                 // shippingName: authState.value.accessTokenPayload?.name || '',
//                 // address1: userHasAddress ? '' : userDataSyncedRef.value?.rememberLastAddress || '',
//                 lat: userDataSyncedRef.value?.rememberLastLat || 0,
//                 lng: userDataSyncedRef.value?.rememberLastLng || 0,
//                 country: userDataSyncedRef.value?.rememberLastCountry || ip.country_code || '',
//                 // state: ip.region_code || '',
//                 // city: ip.city || '',
//             },
//         },
//         onEmit: {
//             addressFormSubmitted: (address) => {
//                 emit('newAddress', address);
//             },
//         },
//     });
// }

</script>

<template>
    <div>
        <AutoForm class="space-y-4"
                  :schema="schema"
                  :dependencies="[
                    {
                        sourceField: 'country',
                        type: DependencyType.HIDES,
                        targetField: 'state',
                        when: (country) => !COUNTRIES_WITH_STATES.includes(country),
                    }
                ]"
                  :field-config="{
                    shippingName: {
                        label: 'Contact Name',
                        inputProps: {
                            placeholder: 'Name Surname',
                            autocomplete: 'shipping name',
                        },
                    },
                    country: {
                        component: AutoFormFieldCountry,
                        label: 'Country',
                        inputProps: {
                            autocomplete: 'shipping country',
                        },
                    },
                    state: {
                        label: addressStateLabel,
                        inputProps: {
                            autocomplete: 'shipping address-level1',
                        },
                    },
                    phoneNumber: {
                        label: 'Contact Number',
                        component: AutoFormFieldPhone
                    },
                    address1: {
                        label: address1Label,
                        inputProps: {
                            placeholder: 'Enter your full address',
                            autocomplete: 'shipping address-line1',
                        },
                    },
                    address2: {
                        label: address2Label,
                        hideLabel: hideAddress2Label,
                        inputProps: {
                            placeholder: hideAddress2Label ? address2Label : '',
                            autocomplete: 'shipping address-line2',
                        },
                    },
                    city: {
                        label: addressCityLabel,
                        inputProps: {
                            autocomplete: 'shipping address-level2',
                        },
                    },
                    postalCode: {
                        label: addressPostalCodeLabel,
                        inputProps: {
                            autocomplete: 'shipping postal-code',
                        },
                    },
                    instructionCategory: {
                        component: AutoFormFieldDropoffOptions,
                        label: ' ',
                    },
                    instructionsForPickupDelivery: {
                        component: 'textarea',
                        label: 'Instructions',
                        inputProps: {
                            placeholder: 'any additional instructions for pickup/delivery',
                        },
                    },
                }"
                  :form="form"
                  @submit="onFormSubmit">

            <template #state="allProps">
                <div>
                    <FormField v-slot="slotProps"
                               name="state"
                               v-if="isStateFieldVisible">
                        <FormItem>
                            <AutoFormLabel v-if="!allProps.config?.hideLabel"
                                           :required="computedStates.length > 0">
                                {{ allProps.config?.label }}
                            </AutoFormLabel>
                            <FormControl>
                                <slot v-bind="slotProps">
                                    <select v-if="computedStates.length > 0"
                                            v-bind="{ ...slotProps.componentField as any, ...allProps.config?.inputProps }"
                                            class="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                            v-model="form.values.state"
                                            @change="(e: Event) => form.setFieldValue('state', (e.target as HTMLSelectElement).value)">
                                        <option value=""
                                                disabled>
                                            Select state
                                        </option>
                                        <option v-for="state in computedStates"
                                                :key="state[0]"
                                                :value="state[0]">
                                            {{ state[1] }}
                                        </option>
                                    </select>
                                    <Input v-else
                                           v-bind="{ ...slotProps.componentField as any, ...allProps.config?.inputProps }" />
                                </slot>
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                </div>
            </template>


            <Button type="submit"
                    class="w-full">
                Save
            </Button>
        </AutoForm>

        <!-- Map Dialog -->
        <Dialog v-model:open="isMapDialogOpen">
            <DialogContent
                           class="flex h-full min-h-full min-w-full flex-col p-0 sm:p-2 md:h-[70vh] md:min-h-[70vh] md:min-w-[80vw] lg:min-w-[50vw]">
                <DialogHeader>
                    <DialogTitle class="flex items-center gap-2">
                        <DoorOpen />
                        Confirm building entrance/door location
                    </DialogTitle>
                </DialogHeader>
                <GeolocationMap :address="getAddress(form.values)"
                                :country="form.values.country!"
                                :lat="lat!"
                                :lng="lng!"
                                @done="onMapConfirm"
                                btn-label="Confirm"
                                class="flex-1 overflow-hidden" />
            </DialogContent>
        </Dialog>
    </div>
</template>